<template>
    <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3 print: border-0 print:shadow-none print:rounded-none">
        <!-- Date and Previous Records Row -->
        <div class="flex gap-3 flex-wrap items-center">
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Data consultației</label>
                <DatePicker v-model="dataConsultModel" dateFormat="dd.mm.yy" showIcon iconDisplay="input" class="w-40 print:w-30"
                    inputClass="w-40 print:w-30" :disabled="disabled"/>
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">CNP Pacient</label>
                <InputText data-cy="cnp" :modelValue="cnpPacient" @update:modelValue="$emit('update:cnpPacient', $event)"
                    placeholder="CNP pacient" maxlength="13" class="w-40 print:w-30" :disabled="disabled" />
            </div>
            <div class="flex flex-col gap-1.5 screen-only">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Vizite anterioare</label>
                <Select data-cy="previous-records" :modelValue="selectedRecord" @update:modelValue="handleRecordSelect" :options="previousRecords"
                    optionLabel="date" placeholder="Selectează" class="w-40 print:w-30" />
            </div>
        </div>

        <!-- Patient Info Row -->
        <div class="flex gap-3 flex-wrap items-center">
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Nume</label>
                <InputText data-cy="last-name" :modelValue="lastNamePacient" @update:modelValue="$emit('update:lastNamePacient', $event)"
                    placeholder="Popescu" class="w-48 print:w-30 font-medium" :disabled="disabled" />
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Prenume</label>
                <InputText data-cy="first-name" :modelValue="firstNamePacient" @update:modelValue="$emit('update:firstNamePacient', $event)"
                    placeholder="Ion" class="w-48 print:w-30 font-medium" :disabled="disabled" />
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Data nașterii</label>
                <DatePicker data-cy="birth-date" v-model="birthDateModel" dateFormat="dd.mm.yy" showIcon iconDisplay="input"
                    class="w-40 print:w-30" inputClass="w-40 print:w-30" :disabled="disabled" />
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Sex</label>
                <div class="flex gap-4 items-center bg-gray-50 rounded-lg px-4 py-2">
                    <div class="flex items-center gap-2">
                        <RadioButton data-cy="sex-f" v-model="sexPacientModel" inputId="sex1" value="F" :disabled="disabled" />
                        <label for="sex1" class="cursor-pointer font-medium">F</label>
                    </div>
                    <div class="flex items-center gap-2">
                        <RadioButton data-cy="sex-m" v-model="sexPacientModel" inputId="sex2" value="M" :disabled="disabled" />
                        <label for="sex2" class="cursor-pointer font-medium">M</label>
                    </div>
                </div>
            </div>
        </div>

        <!-- Additional Info Row -->
        <div class="flex gap-3 flex-wrap items-center">
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Telefon (opțional)</label>
                <InputText data-cy="phone" :modelValue="phonePacient" @update:modelValue="$emit('update:phonePacient', $event)"
                    placeholder="0740123456" class="w-40 print:w-30" :disabled="disabled" />
            </div>
            <div class="flex flex-col gap-1.5 flex-1">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Notițe (opțional)</label>
                <InputText data-cy="notes" :modelValue="notesPacient" @update:modelValue="$emit('update:notesPacient', $event)"
                    placeholder="Observații despre pacient..." class="w-full" :disabled="disabled" />
            </div>
        </div>

        <!-- Measurements Row -->
        <div class="flex gap-3 flex-wrap items-center">
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Înălțime</label>
                <div class="flex items-center gap-2">
                    <InputText data-cy="height" :modelValue="inaltimePacient"
                        @update:modelValue="$emit('update:inaltimePacient', $event)" placeholder="Înălțime"
                        class="w-32 print:w-30" :disabled="disabled"/>
                    <span class="text-sm text-gray-500">cm</span>
                </div>
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">Greutate</label>
                <div class="flex items-center gap-2">
                    <InputText data-cy="weight" :modelValue="greutatePacient"
                        @update:modelValue="$emit('update:greutatePacient', $event)" placeholder="Greutate"
                        class="w-32 print:w-30" :disabled="disabled"/>
                    <span class="text-sm text-gray-500">Kg</span>
                </div>
            </div>
            <div class="flex flex-col gap-1.5">
                <label class="text-xs font-medium text-gray-600 uppercase tracking-wide">BSA (calculat)</label>
                <div class="flex items-center gap-2">
                    <InputText data-cy="bsa" :modelValue="bsaPacient" @update:modelValue="$emit('update:bsaPacient', $event)"
                        placeholder="BSA" class="w-32 pointer-events-none bg-gray-50 print:w-30" readonly disabled />
                    <span class="text-sm text-gray-500">m²</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from "vue";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import RadioButton from "primevue/radiobutton";
import DatePicker from "primevue/datepicker";
import EchoHelper from "../../../../src/helpers.js";

const props = defineProps({
    dataConsult: String,
    cnpPacient: String,
    selectedRecord: Object,
    previousRecords: Array,
    lastNamePacient: String,
    firstNamePacient: String,
    birthDatePacient: String,
    sexPacient: String,
    phonePacient: String,
    notesPacient: String,
    inaltimePacient: String,
    greutatePacient: String,
    bsaPacient: String,
    disabled: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits([
    "update:dataConsult",
    "update:cnpPacient",
    "update:selectedRecord",
    "update:lastNamePacient",
    "update:firstNamePacient",
    "update:birthDatePacient",
    "update:sexPacient",
    "update:phonePacient",
    "update:notesPacient",
    "update:inaltimePacient",
    "update:greutatePacient",
    "update:bsaPacient",
    "load",
]);

const handleRecordSelect = (record) => {
    emit("update:selectedRecord", record);
    if (record) {
        emit("load");
    }
};

const sexPacientModel = computed({
    get: () => props.sexPacient,
    set: (val) => emit("update:sexPacient", val),
});

const dataConsultModel = computed({
    get: () => EchoHelper.parseToDate(props.dataConsult),
    set: (val) => emit("update:dataConsult", val ? EchoHelper.convertToISODate(val) : ""),
});

const birthDateModel = computed({
    get: () => EchoHelper.parseToDate(props.birthDatePacient),
    set: (val) => emit("update:birthDatePacient", val ? EchoHelper.convertToISODate(val) : ""),
});

</script>
